import React, { useState, useContext, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DynamicLayout from "../../components/DynamicLayout";
import CourseHero from '../../components/CourseHero';

import FehuLesson from "../lessons/runes/fehu";
import UruzLesson from "../lessons/runes/uruz";
import ThurisazLesson from "../lessons/runes/thurisaz";
import AnsuzLesson from "../lessons/runes/ansuz";
import RaidhoLesson from "../lessons/runes/raidho";
import KaunanLesson from "../lessons/runes/kaunan";
import GeboLesson from "../lessons/runes/gebo";
import WunjoLesson from "../lessons/runes/wunjo";
import HagalazLesson from "../lessons/runes/hagalaz";
import NauthizLesson from "../lessons/runes/nauthiz";
import IsaLesson from "../lessons/runes/isa";
import JeraLesson from "../lessons/runes/jera";
import EiwazLesson from "../lessons/runes/eihwaz";
import PerthroLesson from "../lessons/runes/perthro";
import AlgizLesson from "../lessons/runes/algiz";
import SowiloLesson from "../lessons/runes/sowilo";
import TiwazLesson from "../lessons/runes/tiwaz";
import BerkanoLesson from "../lessons/runes/berkano";
import EhwazLesson from "../lessons/runes/ehwaz";
import MannazLesson from "../lessons/runes/mannaz";
import LaguzLesson from "../lessons/runes/laguz";
import IngwazLesson from "../lessons/runes/ingwaz";
import DagazLesson from "../lessons/runes/dagaz";
import OthalaLesson from "../lessons/runes/othala";

import StructureLesson from "../lessons/runes/structure";
import IntroductionLesson from "../lessons/runes/introduction";
import EthicsLesson from "../lessons/runes/ethics";
import PreparationLesson from "../lessons/runes/preparation";
import TechniquesLesson from "../lessons/runes/techniques";
import AdvancedLesson from "../lessons/runes/advanced";

import { AcademyContext } from "../../context/academyContext";

// Function to get max questions for each lesson
const getMaxQuestionsForLesson = (lessonNumber) => {
  const questionCounts = {
    1: 5, // Introduction lesson has 5 sections
    2: 5, // Structure lesson has 5 sections
    3: 3, // Fehu lesson has 3 questions
    4: 3, // Uruz lesson has 3 questions
    5: 3, // Thurisaz lesson has 3 questions
    6: 3, // Ansuz lesson has 3 questions
    7: 3, // Raidho lesson has 3 questions
    8: 3, // Kaunan lesson has 3 questions
    9: 3, // Gebo lesson has 3 questions
    10: 3, // Wunjo lesson has 3 questions
    11: 3, // Hagalaz lesson has 3 questions
    12: 3, // Nauthiz lesson has 3 questions
    13: 3, // Isa lesson has 3 questions
    14: 3, // Jera lesson has 3 questions
    15: 3, // Eiwaz lesson has 3 questions
    16: 3, // Perthro lesson has 3 questions
    17: 3, // Algiz lesson has 3 questions
    18: 3, // Sowilo lesson has 3 questions
    19: 3, // Tiwaz lesson has 3 questions
    20: 3, // Berkano lesson has 3 questions
    21: 3, // Ehwaz lesson has 3 questions
    22: 3, // Mannaz lesson has 3 questions
    23: 3, // Laguz lesson has 3 questions
    24: 3, // Ingwaz lesson has 3 questions
    25: 3, // Dagaz lesson has 3 questions
    26: 3, // Othala lesson has 3 questions
    27: 5, // Ethics and Responsibilities lesson has 5 sections
    28: 5, // Preparing for Divination lesson has 5 sections
    29: 5, // Basic Divination Techniques lesson has 5 sections
    30: 5, // Advanced Divination Spreads lesson has 5 sections
  };
  return questionCounts[lessonNumber] || 1;
};

// Function to get chapter and lesson info
const getChapterAndLessonInfo = (lessonNumber) => {
  if (lessonNumber === 1) {
    return { chapter: "Chapter 1: Introduction to Runes", lesson: "Historical Origins and Development" };
  } else if (lessonNumber === 2) {
    return { chapter: "Chapter 1: Introduction to Runes", lesson: "Structure of the Runic Alphabet" };
  } else if (lessonNumber >= 3 && lessonNumber <= 26) {
    const runeNames = [
      "Fehu", "Uruz", "Thurisaz", "Ansuz", "Raidho", "Kaunan", "Gebo", "Wunjo",
      "Hagalaz", "Nauthiz", "Isa", "Jera", "Eiwaz", "Perthro", "Algiz", "Sowilo",
      "Tiwaz", "Berkano", "Ehwaz", "Mannaz", "Laguz", "Ingwaz", "Dagaz", "Othala"
    ];
    return { 
      chapter: "Chapter 2: Learning the Runes", 
      lesson: `${runeNames[lessonNumber - 3]} Rune` 
    };
  } else if (lessonNumber >= 27 && lessonNumber <= 30) {
    const divinationLessons = [
      "Ethics and Responsibilities",
      "Preparing for Divination", 
      "Basic Divination Techniques",
      "Advanced Divination Spreads"
    ];
    return {
      chapter: "Chapter 3: Runic Divination",
      lesson: divinationLessons[lessonNumber - 27]
    };
  }
  return { chapter: "Unknown Chapter", lesson: "Unknown Lesson" };
};

const LessonPage = () => {
  const { progress, updateCourseProgress, updateQuestionProgress, getQuestionProgress } = useContext(AcademyContext);
  const [currentLesson, setCurrentLesson] = useState(progress.runes?.completedLessons + 1 || 1);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [completedQuestions, setCompletedQuestions] = useState([]);
  const [showHero, setShowHero] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const { level } = useParams(); 
  const navigate = useNavigate();

  // Define beginner course completion threshold
  const BEGINNER_COURSE_LESSONS = 30;
  const isIntermediateUnlocked = (progress.runes?.completedLessons || 0) >= BEGINNER_COURSE_LESSONS;

  // Memoize the max questions for current lesson
  const maxQuestions = useMemo(() => getMaxQuestionsForLesson(currentLesson), [currentLesson]);

  useEffect(() => {
    setCurrentLesson(progress.runes?.completedLessons + 1 || 1);
    setCurrentQuestion(0);
  }, [progress.runes?.completedLessons]);

  // Load question progress from Firebase when lesson changes
  useEffect(() => {
    const savedQuestionProgress = getQuestionProgress('runes', currentLesson);
    if (savedQuestionProgress.length > 0) {
      // Pad the array to match maxQuestions length
      const paddedProgress = [...savedQuestionProgress];
      while (paddedProgress.length < maxQuestions) {
        paddedProgress.push(false);
      }
      setCompletedQuestions(paddedProgress);
    } else {
      setCompletedQuestions(Array(maxQuestions).fill(false));
    }
  }, [currentLesson, maxQuestions, getQuestionProgress]);

  // Memoize chapter and lesson info
  const { chapter, lesson } = useMemo(() => getChapterAndLessonInfo(currentLesson), [currentLesson]);

  // Function to update question progress in Firebase
  const updateQuestionProgressInFirebase = async (questionIndex, isCompleted) => {
    await updateQuestionProgress('runes', currentLesson, questionIndex, isCompleted);
  };

  // Enhanced setCompletedQuestions that also saves to Firebase
  const setCompletedQuestionsWithFirebase = (newCompletedQuestions) => {
    // Handle different types of input
    let finalCompletedQuestions;
    
    if (typeof newCompletedQuestions === 'function') {
      // If it's a function, call it with the current state
      finalCompletedQuestions = newCompletedQuestions(completedQuestions);
    } else if (typeof newCompletedQuestions === 'number') {
      // If it's a number, create an array with that many completed questions
      finalCompletedQuestions = Array(completedQuestions.length).fill(false);
      for (let i = 0; i < newCompletedQuestions; i++) {
        finalCompletedQuestions[i] = true;
      }
    } else if (Array.isArray(newCompletedQuestions)) {
      // If it's already an array, use it directly
      finalCompletedQuestions = newCompletedQuestions;
    } else {
      // Fallback: use current state
      finalCompletedQuestions = completedQuestions;
    }
    
    setCompletedQuestions(finalCompletedQuestions);
    
    // Save the last changed question to Firebase
    const lastChangedIndex = finalCompletedQuestions.findIndex((completed, index) => 
      completed !== completedQuestions[index]
    );
    if (lastChangedIndex !== -1) {
      updateQuestionProgressInFirebase(lastChangedIndex, finalCompletedQuestions[lastChangedIndex]);
    }
  };

  const renderLesson = () => {
    switch (currentLesson) {
      case 1:
        return <IntroductionLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
          currentQuestion={currentQuestion}
          onQuestionChange={setCurrentQuestion}
          completedQuestions={completedQuestions}
          setCompletedQuestions={setCompletedQuestions}
        />;
      case 2:
        return <StructureLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
          currentQuestion={currentQuestion}
          onQuestionChange={setCurrentQuestion}
          completedQuestions={completedQuestions}
          setCompletedQuestions={setCompletedQuestions}
        />;
      case 3:
        return <FehuLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 4:
        return <UruzLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 5:
        return <ThurisazLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 6:
        return <AnsuzLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 7:
        return <RaidhoLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 8:
        return <KaunanLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 9:
        return <GeboLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 10:
        return <WunjoLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 11:
        return <HagalazLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 12:
        return <NauthizLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 13:
        return <IsaLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 14:
        return <JeraLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 15:
        return <EiwazLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 16:
        return <PerthroLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 17:
        return <AlgizLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 18:
        return <SowiloLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 19:
        return <TiwazLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 20:
        return <BerkanoLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 21:
        return <EhwazLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 22:
        return <MannazLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 23:
        return <LaguzLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 24:
        return <IngwazLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 25:
        return <DagazLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 26:
        return <OthalaLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
        />;
      case 27:
        return <EthicsLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
          currentQuestion={currentQuestion}
          onQuestionChange={setCurrentQuestion}
          completedQuestions={completedQuestions}
          setCompletedQuestions={setCompletedQuestions}
        />;
      case 28:
        return <PreparationLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
          currentQuestion={currentQuestion}
          onQuestionChange={setCurrentQuestion}
          completedQuestions={completedQuestions}
          setCompletedQuestions={setCompletedQuestions}
        />;
      case 29:
        return <TechniquesLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
          currentQuestion={currentQuestion}
          onQuestionChange={setCurrentQuestion}
          completedQuestions={completedQuestions}
          setCompletedQuestions={setCompletedQuestions}
        />;
      case 30:
        return <AdvancedLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
          currentQuestion={currentQuestion}
          onQuestionChange={setCurrentQuestion}
          completedQuestions={completedQuestions}
          setCompletedQuestions={setCompletedQuestions}
        />;
      default:
        return <StructureLesson 
          level={level} 
          onNextLesson={handleNext} 
          markLessonComplete={markLessonComplete} 
          currentLesson={currentLesson}
          currentQuestion={currentQuestion}
          onQuestionChange={setCurrentQuestion}
          completedQuestions={completedQuestions}
          setCompletedQuestions={setCompletedQuestions}
        />;
    }
  };

  const handleNext = () => {
    setCurrentLesson((prevLesson) => (prevLesson < 26 ? prevLesson + 1 : prevLesson));
  };

  const markLessonComplete = async () => {
    await updateCourseProgress('runes', Math.max(progress.runes?.completedLessons || 0, currentLesson));
  };

  const handleLessonChange = (event) => {
    setCurrentLesson(Number(event.target.value));
  };

  const handleLevelChange = (newLevel) => {
    navigate(`/academy/runes/${newLevel}/lesson?lesson=${currentLesson}`);
  };

  const toggleHero = () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setShowHero(!showHero);
    
    // Reset animation state after animation completes
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };

  return (
    <DynamicLayout>
      <CourseHero
        courseTitle="Runes Course"
        level={level}
        chapter={chapter}
        lesson={lesson}
        currentLesson={currentLesson}
        completedLessons={progress.runes?.completedLessons || 0}
        totalLessons={32}
        completedQuestions={completedQuestions}
        maxQuestions={maxQuestions}
        lessonOptions={
          <>
            <optgroup label="Chapter 1: Introduction to Runes">
              <option value={1}>Historical Origins and Development</option>
            </optgroup>
            <optgroup label="Chapter 2: Learning the Runes">
              <option value={2}>Structure of the Runic Alphabet</option>
              <option value={3}>Fehu</option>
              <option value={4}>Uruz</option>
              <option value={5}>Thurisaz</option>
              <option value={6}>Ansuz</option>
              <option value={7}>Raidho</option>
              <option value={8}>Kaunan</option>
              <option value={9}>Gebo</option>
              <option value={10}>Wunjo</option>
              <option value={11}>Hagalaz</option>
              <option value={12}>Nauthiz</option>
              <option value={13}>Isa</option>
              <option value={14}>Jera</option>
              <option value={15}>Eiwaz</option>
              <option value={16}>Perthro</option>
              <option value={17}>Algiz</option>
              <option value={18}>Sowilo</option>
              <option value={19}>Tiwaz</option>
              <option value={20}>Berkano</option>
              <option value={21}>Ehwaz</option>
              <option value={22}>Mannaz</option>
              <option value={23}>Laguz</option>
              <option value={24}>Ingwaz</option>
              <option value={25}>Dagaz</option>
              <option value={26}>Othala</option>
            </optgroup>
            <optgroup label="Chapter 3: Runic Divination">
              <option value={27}>Ethics and Responsibilities</option>
              <option value={28}>Preparing for Divination</option>
              <option value={29}>Basic Divination Techniques</option>
              <option value={30}>Advanced Divination Spreads</option>
            </optgroup>
          </>
        }
        onLessonChange={handleLessonChange}
        onLevelChange={handleLevelChange}
        isIntermediateUnlocked={isIntermediateUnlocked}
        showHero={showHero}
        isAnimating={isAnimating}
        onToggleHero={toggleHero}
        currentQuestion={currentQuestion}
        onQuestionChange={setCurrentQuestion}
        totalQuestions={maxQuestions}
      />
      <div className="full-screen-image-container hasbackgroundblack margin-top-0 nomaxheight has-text-centered">
        <section className="container has-text-centered">
          {renderLesson()}
        </section>
      </div>
    </DynamicLayout>
  );
};

export default LessonPage;

